
import React from 'react';
import { Battery, Gauge, Zap, AlertTriangle, CheckCircle, MapPin } from 'lucide-react';
import { MovementStatusBadge } from '@/components/bikes/MovementStatusBadge';

interface BikeTechInfoProps {
  batteryPercentage?: number | null;
  averageSpeed?: number | null;
  mileage?: string | number;
  online?: boolean;
  bikeId?: string;
  movementBlocked?: boolean;
  speedLimit?: number | null;
  maxAllowedSpeed?: number;
}

export const BikeTechInfo: React.FC<BikeTechInfoProps> = ({
  batteryPercentage,
  averageSpeed,
  mileage,
  online,
  bikeId,
  movementBlocked = false,
  speedLimit,
  maxAllowedSpeed = 25
}) => {
  const formatMileage = (mileage: string | number | undefined) => {
    if (typeof mileage === 'string') {
      return mileage;
    }
    if (typeof mileage === 'number') {
      return `${mileage} км`;
    }
    return 'Не указан';
  };

  return (
    <div className="space-y-3">
      {/* Статус управления движением */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Статус движения:</span>
        <MovementStatusBadge 
          movementBlocked={movementBlocked}
          speedLimit={speedLimit}
          maxAllowedSpeed={maxAllowedSpeed}
        />
      </div>

      {/* Заряд батареи */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Battery className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Заряд:</span>
        </div>
        <span className="text-sm font-medium">
          {batteryPercentage !== null && batteryPercentage !== undefined ? `${batteryPercentage}%` : 'Н/Д'}
        </span>
      </div>

      {/* Скорость */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gauge className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Скорость:</span>
        </div>
        <span className="text-sm font-medium">
          {averageSpeed ? `${averageSpeed} км/ч` : 'Н/Д'}
        </span>
      </div>

      {/* Пробег */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Пробег:</span>
        </div>
        <span className="text-sm font-medium">{formatMileage(mileage)}</span>
      </div>

      {/* Статус подключения */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Подключение:</span>
        </div>
        <span className={`text-sm font-medium ${online ? 'text-green-600' : 'text-red-600'}`}>
          {online !== undefined ? (online ? 'Онлайн' : 'Офлайн') : 'Н/Д'}
        </span>
      </div>

      {/* Дополнительное предупреждение при блокировке */}
      {movementBlocked && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mt-3">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4" />
            <span>Внимание: движение велосипеда заблокировано администратором</span>
          </div>
        </div>
      )}

      {/* Информация об ограничении скорости */}
      {speedLimit && speedLimit > 0 && !movementBlocked && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
          <div className="flex items-center gap-2 text-sm text-yellow-800">
            <Gauge className="h-4 w-4" />
            <span>Установлено ограничение скорости: {speedLimit} км/ч</span>
          </div>
        </div>
      )}
    </div>
  );
};
